import type { AppRouter } from '@app/api'
import { NAMED_PAPERS } from '@app/shared'
import { useQuery } from '@tanstack/react-query'
import type { inferRouterOutputs } from '@trpc/server'
import { useEffect, useMemo, useRef } from 'react'
import { registerDocHostClient } from '../../lib/doc-hosts'
import { fetchHtmlPagePreview } from '../../lib/html-page-preview'
import { useTRPC } from '../../lib/trpc'
import { trpcClient } from '../../lib/trpc-client'
import type { DocHostClient } from '../../ui/formats/DocView'
import { DocView } from '../../ui/formats/DocView'
import { PublicReaderHeader } from './PublicReaderHeader'
import { SharedBookPage } from './SharedBookPage'

type PublicRecord = inferRouterOutputs<AppRouter>['share']['publicRecord']

function pdfBlob(base64: string): Blob {
  const bytes = Uint8Array.from(atob(base64), (character) =>
    character.charCodeAt(0),
  )
  return new Blob([bytes], { type: 'application/pdf' })
}

export function SharedRecordPage({ token }: { token: string }) {
  const trpc = useTRPC()
  const shared = useQuery({
    ...trpc.share.publicRecord.queryOptions({ token }),
    retry: false,
  })
  /* Opening the page is one open, counted once per load. The record is polled
     while it is read, so the count is its own call rather than the read's; a
     guard holds it to one where a development render runs effects twice. */
  const counted = useRef<string | null>(null)
  useEffect(() => {
    if (counted.current === token) return
    counted.current = token
    trpcClient.share.publicOpened.mutate({ token }).catch(() => {
      /* A count that fails costs the reader nothing. */
    })
  }, [token])

  if (shared.isPending) {
    return (
      <div className="sharedbook" aria-busy="true">
        <PublicReaderHeader />
      </div>
    )
  }
  if (shared.error) {
    const ended = shared.error.message === 'This link has ended.'
    return (
      <div className="sharedbook">
        <PublicReaderHeader />
        <div className="sharedbook__gone">
          <h1>
            {ended
              ? 'This link has ended.'
              : 'This link does not open anything.'}
          </h1>
          {!ended ? <p>Check that the address was copied completely.</p> : null}
        </div>
      </div>
    )
  }
  if (shared.data.kind === 'book') return <SharedBookPage token={token} />
  return <SharedDoc token={token} shared={shared.data} />
}

function SharedDoc({
  token,
  shared,
}: {
  token: string
  shared: Extract<PublicRecord, { kind: 'page' | 'deck' }>
}) {
  const record = shared.record
  const host = `public-studio-${shared.kind}`
  const client = useMemo<DocHostClient>(() => {
    return {
      view: () => ({
        queryKey: ['public-studio-record', token],
        queryFn: async () => {
          if ('slides' in record) {
            return {
              format: 'slides' as const,
              title: record.title,
              facts: [],
              downloads: [{ variant: 'slides', label: 'Slides' }],
              extra: { slides: record.slides, canWrite: false },
            }
          }
          const paper = record.paper
          const geometry = !paper
            ? undefined
            : paper.name === 'custom'
              ? { widthMm: paper.widthMm, heightMm: paper.heightMm }
              : NAMED_PAPERS[paper.name]
          return {
            format: 'html' as const,
            title: record.title,
            facts: [],
            paper: geometry,
            orientation: record.orientation,
            width: record.width,
            extra: { kind: record.kind },
          }
        },
      }),
      previewTicket: async (_ref, slideId) => {
        return trpcClient.share.publicPreviewTicket.mutate({ token, slideId })
      },
      ...(!('slides' in record)
        ? {
            previewPage: () =>
              fetchHtmlPagePreview(() =>
                trpcClient.share.publicPreviewTicket.mutate({ token }),
              ),
          }
        : {}),
      download: async () => {
        const result = await trpcClient.share.publicDownload.mutate({ token })
        return { blob: pdfBlob(result.base64), filename: result.filename }
      },
    }
  }, [record, token])
  registerDocHostClient(host, client)
  /* A screen page on a full-window link is read as a site: the page is the
     whole window and Studio draws nothing around it. */
  if (!('slides' in record) && record.fullWindow && !record.paper) {
    return (
      <div className="sharedbook sharedbook--bare">
        <main className="sharedbook__site">
          <DocView doc={{ host, spaceId: record.spaceId, key: record.key }} />
        </main>
      </div>
    )
  }
  return (
    <div className="sharedbook">
      <PublicReaderHeader />
      <main className="sharedbook__main">
        <DocView doc={{ host, spaceId: record.spaceId, key: record.key }} />
      </main>
    </div>
  )
}
